/**
 * InstallPrompt — Android / Chrome / Edge / desktop "Install GoalDigger" card.
 *
 * Captures `beforeinstallprompt` and surfaces an engaging card once the user
 * is signed in + has at least one real goal. Snooze 7 days / lifetime cap 4 /
 * permanent dismiss option, same shared state as the iOS hint.
 *
 * The actual reward (+100 pts) is granted by `usePwaInstallReward` on the
 * `appinstalled` event, not by this card.
 */
import { useEffect, useState } from "react";
import { Download, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useGoalQueries } from "@/hooks/useGoalQueries";
import { useIsStandalone } from "@/hooks/useIsStandalone";
import { isLovablePreviewHost } from "@/lib/platform";
import {
  isEligibleToPrompt,
  snoozeInstallPrompt,
  permanentlyDismissInstallPrompt,
} from "@/lib/installPromptState";
import { HomeScreenPreview } from "./HomeScreenPreview";
import { logger } from "@/utils/logger";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function InstallPrompt() {
  const { user } = useAuth();
  const { goals } = useGoalQueries();
  const isStandalone = useIsStandalone();

  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [armed, setArmed] = useState(false);
  const [, forceCheck] = useState(0);

  useEffect(() => {
    if (isLovablePreviewHost() || isStandalone) return;
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferred(null);
      // Reward is granted by usePwaInstallReward; we just clear the card.
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [isStandalone]);

  // Listen for "install moment" events to arm the prompt early.
  useEffect(() => {
    const onArm = () => { setArmed(true); forceCheck((n) => n + 1); };
    window.addEventListener("install-prompt:arm", onArm);
    return () => window.removeEventListener("install-prompt:arm", onArm);
  }, []);

  const hasRealGoal = (goals || []).some((g: any) => !g?.isExample);
  const baseEligible =
    !!deferred &&
    !isStandalone &&
    !isLovablePreviewHost() &&
    !!user &&
    hasRealGoal;

  if (!baseEligible) return null;
  if (!isEligibleToPrompt({ armed })) return null;

  const handleInstall = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      logger.info("[PWA] install choice:", choice.outcome);
      if (choice.outcome === "dismissed") {
        snoozeInstallPrompt();
      }
    } catch (e) {
      logger.warn("[PWA] install prompt failed:", e);
    } finally {
      setDeferred(null);
      forceCheck((n) => n + 1);
    }
  };

  const handleSnooze = () => {
    snoozeInstallPrompt();
    setArmed(false);
    forceCheck((n) => n + 1);
  };

  const handleDismissForever = () => {
    permanentlyDismissInstallPrompt();
    setArmed(false);
    forceCheck((n) => n + 1);
  };

  const headline = "Install GoalDigger";

  const subheadline = "1-tap access, full-screen mode, and a +100 pt bonus.";

  return (
    <div
      role="dialog"
      aria-label="Install GoalDigger"
      className="fixed z-[60] left-3 right-3 md:left-auto md:right-6 md:w-[26rem] rounded-2xl border border-border bg-background shadow-2xl p-4 animate-in slide-in-from-bottom-4"
      style={{ bottom: "calc(5.5rem + env(safe-area-inset-bottom, 0px))" }}
    >
      <button
        onClick={handleSnooze}
        aria-label="Snooze for 7 days"
        className="absolute top-2 right-2 p-1.5 rounded-md hover:bg-muted text-muted-foreground"
        style={{ touchAction: "manipulation" }}
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <div className="h-10 w-10 rounded-xl bg-orange-500/15 flex items-center justify-center flex-shrink-0">
          <Download className="h-5 w-5 text-orange-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-foreground">{headline}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{subheadline}</p>

          <HomeScreenPreview />

          <div className="mt-3 flex items-center gap-2 rounded-lg bg-orange-500/10 px-3 py-2">
            <Sparkles className="h-4 w-4 text-orange-600 flex-shrink-0" />
            <span className="text-[11px] font-semibold text-orange-900 dark:text-orange-100">
              You'll earn <strong>+100 points</strong> automatically the first time you open it from your home screen.
            </span>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleInstall}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              style={{ touchAction: "manipulation" }}
            >
              <Download className="h-4 w-4 mr-1.5" />
              Install now
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSnooze}
              style={{ touchAction: "manipulation" }}
            >
              Remind me later
            </Button>
          </div>
          <div className="mt-2">
            <button
              type="button"
              onClick={handleDismissForever}
              className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2"
              style={{ touchAction: "manipulation" }}
            >
              Don't show again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
